import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  AlertTriangle,
  CheckCircle2,
  CopyIcon,
  ExternalLinkIcon,
  Loader2,
  PlusIcon,
  RefreshCwIcon,
  SearchIcon,
  XIcon,
} from 'lucide-react'
import ConfirmationModal from './ConfirmationModal'
import { APP_SUBDOMAIN_TAKEN_MESSAGE } from './TenantProductsField'
import { apiClient, ApiError, ExpertCounts, ExpertItem, ExpertSignInSettings } from '../lib/api'
import { EXPERT_APP_DOMAIN } from '../lib/utils'

interface TenantDomainExpertsTabProps {
  tenantId: string
}

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function errorMessage(err: unknown, fallback: string): string {
  return err instanceof Error && err.message ? err.message : fallback
}

function plural(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? '' : 's'}`
}

function parseDomains(text: string): string[] {
  return Array.from(
    new Set(
      text
        .split(/[\s,]+/)
        .map((d) => d.trim().toLowerCase())
        .filter(Boolean),
    ),
  )
}

function expertStatus(expert: ExpertItem): { label: string; className: string } {
  if (expert.status === 'active') return { label: 'Active', className: 'bg-green-100 text-green-700' }
  if (expert.status === 'disabled') return { label: 'Disabled', className: 'bg-gray-100 text-gray-600' }
  if (expert.invite_state === 'pending') return { label: 'Invite sent', className: 'bg-blue-100 text-blue-700' }
  return { label: 'Invite expired', className: 'bg-yellow-50 text-yellow-700' }
}

const EMAIL_FAILED_NOTE = "We couldn't send the email. Copy the link and send it yourself."

// ── Small building blocks ────────────────────────────────────────────────────

function Section({ title, description, children }: { title: string; description?: string; children: React.ReactNode }) {
  return (
    <div className="bg-white border border-gray-100 rounded-xl p-5 mb-5">
      <h3 className="text-sm font-semibold text-gray-900 mb-1">{title}</h3>
      {description && <p className="text-sm text-gray-500 mb-4">{description}</p>}
      {children}
    </div>
  )
}

interface ToggleProps {
  checked: boolean
  onChange: (value: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}

function Toggle({ checked, onChange, label, description, disabled }: ToggleProps) {
  return (
    <div className="flex items-start justify-between gap-4 py-3">
      <div>
        <p className={`text-sm font-medium ${disabled ? 'text-gray-400' : 'text-gray-900'}`}>{label}</p>
        {description && <p className="text-xs text-gray-500 mt-0.5">{description}</p>}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label}
        disabled={disabled}
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 flex-shrink-0 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
          checked ? 'bg-primary-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
            checked ? 'translate-x-5' : 'translate-x-0.5'
          }`}
        />
      </button>
    </div>
  )
}

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      setCopied(false)
    }
  }

  return (
    <button
      type="button"
      onClick={handleCopy}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
    >
      {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" /> : <CopyIcon className="h-3.5 w-3.5" />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  )
}

function InlineError({ message }: { message: string }) {
  return (
    <p className="mt-2 flex items-center gap-1.5 text-xs text-red-600">
      <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" /> {message}
    </p>
  )
}

function InlineSuccess({ message }: { message: string }) {
  return (
    <p className="mt-2 flex items-center gap-1.5 text-xs text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" /> {message}
    </p>
  )
}

const primaryButtonClass =
  'inline-flex items-center justify-center gap-2 px-4 py-2 text-sm font-semibold text-white bg-primary-600 rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap'
const inputClass =
  'block w-full rounded-md border border-gray-300 shadow-sm focus:outline-none focus:border-gray-400 sm:text-sm px-3 py-2 disabled:bg-gray-50 disabled:text-gray-400'

// ── (a) Google Workspace sign-in ─────────────────────────────────────────────

function SignInSection({ tenantId, settings }: { tenantId: string; settings: ExpertSignInSettings }) {
  const queryClient = useQueryClient()
  const [googleEnabled, setGoogleEnabled] = useState(settings.google_sso_enabled)
  const [domainsText, setDomainsText] = useState(settings.allowed_google_domains.join(', '))
  const [autoJoin, setAutoJoin] = useState(settings.auto_join_enabled)
  const [passwordEnabled, setPasswordEnabled] = useState(settings.password_login_enabled)

  const settingsKey = JSON.stringify(settings)
  useEffect(() => {
    setGoogleEnabled(settings.google_sso_enabled)
    setDomainsText(settings.allowed_google_domains.join(', '))
    setAutoJoin(settings.auto_join_enabled)
    setPasswordEnabled(settings.password_login_enabled)
  }, [settingsKey])

  const domains = parseDomains(domainsText)
  const effectiveAutoJoin = googleEnabled && autoJoin
  const noMethod = !googleEnabled && !passwordEnabled
  const missingDomains = googleEnabled && domains.length === 0
  const dirty =
    googleEnabled !== settings.google_sso_enabled ||
    passwordEnabled !== settings.password_login_enabled ||
    effectiveAutoJoin !== settings.auto_join_enabled ||
    domains.join(',') !== settings.allowed_google_domains.join(',')

  const mutation = useMutation({
    mutationFn: () =>
      apiClient.updateDomainExpertSignIn(tenantId, {
        google_sso_enabled: googleEnabled,
        allowed_google_domains: domains,
        auto_join_enabled: effectiveAutoJoin,
        password_login_enabled: passwordEnabled,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['domainExpert', tenantId] })
    },
  })

  return (
    <Section
      title="Google Workspace sign-in"
      description="Choose how users sign in to the expert app. Domains are managed here only."
    >
      <div className="divide-y divide-gray-50">
        <Toggle
          checked={googleEnabled}
          onChange={setGoogleEnabled}
          label="Google Workspace sign-in"
          description="Users sign in with their company Google account."
        />
        <div className="py-3">
          <label htmlFor="allowed-google-domains" className={`block text-sm font-medium ${googleEnabled ? 'text-gray-900' : 'text-gray-400'}`}>
            Allowed Google domains
          </label>
          <input
            id="allowed-google-domains"
            type="text"
            value={domainsText}
            onChange={(e) => setDomainsText(e.target.value)}
            disabled={!googleEnabled}
            placeholder="acme.com, acme.co.uk"
            className={`mt-1 ${inputClass}`}
          />
          <p className="mt-1 text-xs text-gray-500">Comma-separated. Personal Gmail addresses are not allowed.</p>
          {googleEnabled && domains.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {domains.map((domain) => (
                <span key={domain} className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                  {domain}
                </span>
              ))}
            </div>
          )}
        </div>
        <Toggle
          checked={effectiveAutoJoin}
          onChange={setAutoJoin}
          disabled={!googleEnabled}
          label="Let anyone with an allowed company Google account join automatically"
          description="Off means only invited users can sign in."
        />
        <Toggle checked={passwordEnabled} onChange={setPasswordEnabled} label="Allow password sign-in" />
      </div>

      {noMethod && <InlineError message="At least one sign-in method must stay on." />}
      {missingDomains && <InlineError message="Add at least one allowed domain to use Google sign-in." />}
      {mutation.error ? <InlineError message={errorMessage(mutation.error, 'Failed to save sign-in settings')} /> : null}
      {mutation.isSuccess && !dirty && <InlineSuccess message="Sign-in settings saved." />}

      <div className="mt-4">
        <button
          type="button"
          onClick={() => mutation.mutate()}
          disabled={!dirty || noMethod || missingDomains || mutation.isLoading}
          className={primaryButtonClass}
        >
          {mutation.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          {mutation.isLoading ? 'Saving…' : 'Save sign-in settings'}
        </button>
      </div>
    </Section>
  )
}

// ── (b) Expert app address ───────────────────────────────────────────────────

function AppAddressSection({ tenantId, appSubdomain, loginUrl }: { tenantId: string; appSubdomain: string; loginUrl: string }) {
  const queryClient = useQueryClient()
  const [value, setValue] = useState(appSubdomain)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setValue(appSubdomain)
    setError(null)
  }, [appSubdomain])

  const mutation = useMutation({
    mutationFn: () => apiClient.updateDomainExpertApp(tenantId, { app_subdomain: value }),
    onSuccess: () => {
      setConfirmOpen(false)
      queryClient.invalidateQueries({ queryKey: ['domainExpert', tenantId] })
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
    onError: (err: Error) => {
      setConfirmOpen(false)
      setError(
        (err as ApiError).code === 'app_subdomain_taken'
          ? APP_SUBDOMAIN_TAKEN_MESSAGE
          : errorMessage(err, 'Failed to update the expert app address'),
      )
    },
  })

  const changed = value.length > 0 && value !== appSubdomain

  return (
    <Section title="Expert app address" description="Where users open and install the expert app.">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">Sign-in link</p>
      <div className="flex items-center gap-2 mb-5">
        <code className="flex-1 min-w-0 truncate text-sm bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 text-gray-800">
          {loginUrl}
        </code>
        <CopyButton value={loginUrl} />
        <a
          href={loginUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors flex-shrink-0"
        >
          Open
          <ExternalLinkIcon className="h-3.5 w-3.5" />
        </a>
      </div>

      <label htmlFor="expert-app-subdomain" className="block text-sm font-medium text-gray-900">
        Address
      </label>
      <div className="mt-1 flex flex-col sm:flex-row gap-2">
        <div className="flex flex-1 rounded-md shadow-sm">
          <input
            id="expert-app-subdomain"
            type="text"
            value={value}
            onChange={(e) => {
              setValue(e.target.value.toLowerCase().replace(/\s+/g, ''))
              setError(null)
            }}
            className={`block w-full min-w-0 flex-1 rounded-none rounded-l-md border px-3 py-2 sm:text-sm focus:outline-none ${
              error ? 'border-red-400 focus:border-red-500' : 'border-gray-300 focus:border-gray-400'
            }`}
          />
          <span className="inline-flex items-center rounded-r-md border border-l-0 border-gray-300 bg-gray-50 px-3 text-sm text-gray-500">
            .{EXPERT_APP_DOMAIN}
          </span>
        </div>
        <button
          type="button"
          onClick={() => setConfirmOpen(true)}
          disabled={!changed || mutation.isLoading}
          className={primaryButtonClass}
        >
          {mutation.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
          Change address
        </button>
      </div>
      <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-700">
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        Changing the address breaks expert apps that are already installed.
      </p>
      {error && <InlineError message={error} />}
      {mutation.isSuccess && !changed && <InlineSuccess message="Expert app address updated." />}

      <ConfirmationModal
        isOpen={confirmOpen}
        onClose={() => !mutation.isLoading && setConfirmOpen(false)}
        onConfirm={() => mutation.mutate()}
        title="Change expert app address?"
        message={`Installed expert apps stop working at ${appSubdomain}.${EXPERT_APP_DOMAIN}. Users must open ${value}.${EXPERT_APP_DOMAIN} and install the app again. The new address also needs its DNS and hosting set up.`}
        confirmText="Change address"
        type="warning"
        isLoading={mutation.isLoading}
      />
    </Section>
  )
}

// ── (c) Experts ──────────────────────────────────────────────────────────────

interface LinkResult {
  title: string
  url: string
  note: string
}

interface PendingConfirm {
  expertId: string
  title: string
  message: string
  confirmText: string
  type: 'danger' | 'warning'
  run: () => Promise<void>
}

function ExpertsSection({ tenantId, passwordLoginEnabled }: { tenantId: string; passwordLoginEnabled: boolean }) {
  const queryClient = useQueryClient()
  const [search, setSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [showAdd, setShowAdd] = useState(false)
  const [newName, setNewName] = useState('')
  const [newEmail, setNewEmail] = useState('')
  const [addError, setAddError] = useState<string | null>(null)
  const [linkResult, setLinkResult] = useState<LinkResult | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  const { data, isLoading, error, isFetching } = useQuery({
    queryKey: ['domainExperts', tenantId, debouncedSearch],
    queryFn: () => apiClient.listDomainExperts(tenantId, { search: debouncedSearch || undefined, limit: 100 }),
    keepPreviousData: true,
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['domainExperts', tenantId] })
    queryClient.invalidateQueries({ queryKey: ['domainExpert', tenantId] })
  }

  const createMutation = useMutation({
    mutationFn: () =>
      apiClient.createDomainExpert(tenantId, { full_name: newName.trim(), email: newEmail.trim(), send_invite: true }),
    onSuccess: (result) => {
      setShowAdd(false)
      setNewName('')
      setNewEmail('')
      setAddError(null)
      if (result.invite_url) {
        setLinkResult({
          title: `Invite link for ${result.expert.email}`,
          url: result.invite_url,
          note: result.email_sent ? 'Invite email sent. You can also copy the link.' : EMAIL_FAILED_NOTE,
        })
      }
      refresh()
    },
    onError: (err: Error) => {
      setAddError(
        (err as ApiError).code === 'duplicate_email'
          ? 'This email already has access.'
          : errorMessage(err, 'Failed to add user'),
      )
    },
  })

  const runAction = async (expertId: string, action: () => Promise<void>) => {
    setBusyId(expertId)
    setActionError(null)
    try {
      await action()
      refresh()
    } catch (err) {
      setActionError(errorMessage(err, 'Action failed'))
    } finally {
      setBusyId(null)
      setPendingConfirm(null)
    }
  }

  const resendInvite = (expert: ExpertItem) =>
    runAction(expert.id, async () => {
      const result = await apiClient.sendDomainExpertInvite(tenantId, expert.id, true)
      setLinkResult({
        title: `New invite for ${expert.email}`,
        url: result.invite_url,
        note: result.email_sent ? 'Invite email sent. Earlier invite links no longer work.' : EMAIL_FAILED_NOTE,
      })
    })

  const resetPassword = (expert: ExpertItem) =>
    runAction(expert.id, async () => {
      const result = await apiClient.resetDomainExpertPassword(tenantId, expert.id, true)
      setLinkResult({
        title: `Password reset link for ${expert.email}`,
        url: result.reset_url,
        note: result.email_sent ? 'Reset email sent. You can also copy the link.' : EMAIL_FAILED_NOTE,
      })
    })

  const restore = (expert: ExpertItem) =>
    runAction(expert.id, async () => {
      await apiClient.updateDomainExpert(tenantId, expert.id, { status: 'active' })
    })

  const confirmCopyInvite = (expert: ExpertItem) =>
    setPendingConfirm({
      expertId: expert.id,
      title: 'Copy invite link',
      message: `This creates a new invite link. The link in the email already sent to ${expert.email} stops working.`,
      confirmText: 'Create link',
      type: 'warning',
      run: async () => {
        const result = await apiClient.sendDomainExpertInvite(tenantId, expert.id, false)
        setLinkResult({
          title: `Invite link for ${expert.email}`,
          url: result.invite_url,
          note: 'Copy the link and send it to the user.',
        })
      },
    })

  const confirmCancelInvite = (expert: ExpertItem) =>
    setPendingConfirm({
      expertId: expert.id,
      title: 'Cancel invite',
      message: `The invite link sent to ${expert.email} stops working. The user stays on the list until removed.`,
      confirmText: 'Cancel invite',
      type: 'danger',
      run: async () => {
        await apiClient.cancelDomainExpertInvite(tenantId, expert.id)
      },
    })

  const confirmSignOut = (expert: ExpertItem) =>
    setPendingConfirm({
      expertId: expert.id,
      title: 'Sign out everywhere',
      message: `Signs ${expert.full_name} out on every device. They can sign in again.`,
      confirmText: 'Sign out',
      type: 'warning',
      run: async () => {
        await apiClient.revokeDomainExpertSessions(tenantId, expert.id)
      },
    })

  const confirmDisable = (expert: ExpertItem) =>
    setPendingConfirm({
      expertId: expert.id,
      title: 'Disable user',
      message: `${expert.full_name} is signed out and can't use the expert app until restored.`,
      confirmText: 'Disable',
      type: 'warning',
      run: async () => {
        await apiClient.updateDomainExpert(tenantId, expert.id, { status: 'disabled' })
      },
    })

  const confirmRemove = (expert: ExpertItem) =>
    setPendingConfirm({
      expertId: expert.id,
      title: 'Remove user',
      message: `Permanently removes ${expert.full_name} (${expert.email}) and signs them out. This cannot be undone.`,
      confirmText: 'Remove',
      type: 'danger',
      run: async () => {
        await apiClient.deleteDomainExpert(tenantId, expert.id)
      },
    })

  const rowActions = (expert: ExpertItem): { label: string; onClick: () => void; danger?: boolean }[] => {
    const remove = { label: 'Remove', onClick: () => confirmRemove(expert), danger: true }
    if (expert.status === 'disabled') {
      return [{ label: 'Restore', onClick: () => restore(expert) }, remove]
    }
    if (expert.status === 'active') {
      return [
        ...(passwordLoginEnabled ? [{ label: 'Reset password', onClick: () => resetPassword(expert) }] : []),
        { label: 'Sign out everywhere', onClick: () => confirmSignOut(expert) },
        { label: 'Disable', onClick: () => confirmDisable(expert), danger: true },
        remove,
      ]
    }
    if (expert.invite_state === 'pending') {
      return [
        { label: 'Resend invite', onClick: () => resendInvite(expert) },
        { label: 'Copy invite link', onClick: () => confirmCopyInvite(expert) },
        { label: 'Cancel invite', onClick: () => confirmCancelInvite(expert), danger: true },
        remove,
      ]
    }
    return [{ label: 'Send new invite', onClick: () => resendInvite(expert) }, remove]
  }

  const experts = data?.items ?? []
  const counts = data?.counts

  return (
    <Section title="Users" description="People who can use this tenant's expert app.">
      {/* Toolbar */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
        <div className="relative flex-1 sm:max-w-xs">
          <SearchIcon className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or email"
            className={`${inputClass} pl-9`}
          />
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={refresh}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
          >
            <RefreshCwIcon className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
          <button
            type="button"
            onClick={() => {
              setShowAdd(true)
              setAddError(null)
            }}
            className={primaryButtonClass}
          >
            <PlusIcon className="h-4 w-4" />
            Add user
          </button>
        </div>
      </div>

      {counts && (
        <div className="flex flex-wrap items-center gap-2 text-xs mb-4">
          <span className="inline-flex px-2 py-0.5 rounded-full bg-green-100 text-green-700">{counts.active} active</span>
          <span className="inline-flex px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">{counts.invited} invited</span>
          <span className="inline-flex px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">{counts.disabled} disabled</span>
        </div>
      )}

      {/* Add expert */}
      {showAdd && (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            createMutation.mutate()
          }}
          className="mb-4 bg-gray-50 border border-gray-200 rounded-lg px-4 py-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label htmlFor="new-expert-name" className="block text-sm font-medium text-gray-700">
                Name
              </label>
              <input
                id="new-expert-name"
                type="text"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                className={`mt-1 ${inputClass}`}
                autoFocus
                required
              />
            </div>
            <div>
              <label htmlFor="new-expert-email" className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <input
                id="new-expert-email"
                type="email"
                value={newEmail}
                onChange={(e) => {
                  setNewEmail(e.target.value)
                  setAddError(null)
                }}
                className={`mt-1 ${inputClass}`}
                required
              />
            </div>
          </div>
          {addError && <InlineError message={addError} />}
          <div className="mt-3 flex items-center gap-2">
            <button
              type="submit"
              disabled={!newName.trim() || !newEmail.trim() || createMutation.isLoading}
              className={primaryButtonClass}
            >
              {createMutation.isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {createMutation.isLoading ? 'Adding…' : 'Add and send invite'}
            </button>
            <button
              type="button"
              onClick={() => setShowAdd(false)}
              disabled={createMutation.isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {/* Link result (invite or reset) */}
      {linkResult && (
        <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium text-gray-900">{linkResult.title}</p>
              <p className="mt-0.5 text-xs text-gray-600">{linkResult.note}</p>
              <div className="mt-2 flex items-center gap-2">
                <code className="flex-1 min-w-0 truncate text-xs bg-white border border-gray-200 rounded px-2 py-1.5 text-gray-800">
                  {linkResult.url}
                </code>
                <CopyButton value={linkResult.url} />
              </div>
            </div>
            <button
              type="button"
              onClick={() => setLinkResult(null)}
              className="p-1 text-gray-400 hover:text-gray-600"
              aria-label="Dismiss"
            >
              <XIcon className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {actionError && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">{actionError}</div>
      )}

      {/* List */}
      {isLoading ? (
        <div className="px-6 py-10 text-center">
          <Loader2 className="h-6 w-6 text-gray-400 mx-auto mb-2 animate-spin" />
          <p className="text-sm text-gray-500">Loading users...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
          {errorMessage(error, 'Failed to load users')}
        </div>
      ) : experts.length === 0 ? (
        <div className="px-6 py-10 text-center text-sm text-gray-400">
          {debouncedSearch ? `No users matching "${debouncedSearch}"` : 'No users yet.'}
        </div>
      ) : (
        <div className="overflow-x-auto -mx-5 sm:mx-0">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-100 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Email</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Last active</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {experts.map((expert) => {
                const badge = expertStatus(expert)
                const busy = busyId === expert.id
                return (
                  <tr key={expert.id} className="hover:bg-gray-50/60 transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{expert.full_name}</td>
                    <td className="px-4 py-3 text-sm text-gray-600">{expert.email}</td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full whitespace-nowrap ${badge.className}`}>
                        {badge.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(expert.last_seen_at ?? expert.last_login_at)}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-1">
                        {busy && <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />}
                        {rowActions(expert).map((action) => (
                          <button
                            key={action.label}
                            type="button"
                            onClick={action.onClick}
                            disabled={busy}
                            className={`text-xs font-medium whitespace-nowrap transition-colors disabled:opacity-50 ${
                              action.danger ? 'text-red-600 hover:text-red-800' : 'text-primary-600 hover:text-primary-800'
                            }`}
                          >
                            {action.label}
                          </button>
                        ))}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          {data && data.total > experts.length && (
            <p className="px-4 pt-3 text-xs text-gray-400">
              Showing {experts.length} of {data.total}. Use search to narrow the list.
            </p>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={pendingConfirm !== null}
        onClose={() => !busyId && setPendingConfirm(null)}
        onConfirm={() => pendingConfirm && runAction(pendingConfirm.expertId, pendingConfirm.run)}
        title={pendingConfirm?.title ?? ''}
        message={pendingConfirm?.message ?? ''}
        confirmText={pendingConfirm?.confirmText}
        type={pendingConfirm?.type ?? 'danger'}
        isLoading={!!busyId && busyId === pendingConfirm?.expertId}
      />
    </Section>
  )
}

// ── (d) Summary ──────────────────────────────────────────────────────────────

function SummarySection({
  tenantId,
  counts,
  enabledAt,
  enabledByEmail,
}: {
  tenantId: string
  counts: ExpertCounts
  enabledAt: string | null
  enabledByEmail: string | null
}) {
  const queryClient = useQueryClient()
  const [regenerateOpen, setRegenerateOpen] = useState(false)
  const [regenerateNote, setRegenerateNote] = useState<string | null>(null)
  const { data: summary, error: summaryError } = useQuery({
    queryKey: ['expertAgentSummary', tenantId],
    queryFn: () => apiClient.getExpertAgentSummary(tenantId),
    retry: false,
    refetchInterval: (data) => (data && data.documents_processing > 0 ? 5000 : false),
  })
  const { data: flags, isLoading: flagsLoading, error: flagsError } = useQuery({
    queryKey: ['expertAgentOpenFlags', tenantId],
    queryFn: () => apiClient.getExpertAgentOpenFlags(tenantId, 5),
    retry: false,
  })

  const stats = [
    { label: 'Active users', value: counts.active },
    { label: 'Invited', value: counts.invited },
    { label: 'Documents ready', value: summary?.documents_ready },
    { label: 'Saved answers', value: summary?.saved_answers },
    { label: 'Open flags', value: summary?.open_flags },
  ]
  const recentFlags = (flags?.items ?? []).slice(0, 5)

  const regenerateMutation = useMutation({
    mutationFn: () => apiClient.regenerateExpertEmbeddings(tenantId),
    onSuccess: (result) => {
      setRegenerateOpen(false)
      setRegenerateNote(
        `Regenerating ${plural(result.saved_answers, 'saved answer')} and ${plural(result.documents, 'document')}.`,
      )
      queryClient.invalidateQueries({ queryKey: ['expertAgentSummary', tenantId] })
    },
    onError: () => setRegenerateOpen(false),
  })

  const missingAnswers = summary?.saved_answers_missing_embeddings ?? 0
  const missingDocuments = summary?.documents_missing_embeddings ?? 0
  const missingText = [
    missingAnswers > 0 ? plural(missingAnswers, 'saved answer') : null,
    missingDocuments > 0 ? plural(missingDocuments, 'document') : null,
  ]
    .filter(Boolean)
    .join(' and ')
  const documentTotal = summary ? summary.documents_ready + summary.documents_failed : 0

  return (
    <Section title="Summary">
      {enabledAt && (
        <p className="text-xs text-gray-500 mb-3">
          Enabled {formatDate(enabledAt)}
          {enabledByEmail ? ` by ${enabledByEmail}` : ''}
        </p>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-3">
        {stats.map((stat) => (
          <div key={stat.label} className="border border-gray-100 rounded-lg px-4 py-3">
            <p className="text-2xl font-bold text-gray-900">{stat.value ?? '—'}</p>
            <p className="text-xs text-gray-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>
      {summary && (summary.documents_processing > 0 || summary.documents_failed > 0) && (
        <p className="text-xs text-gray-500 mb-3">
          {summary.documents_processing} document{summary.documents_processing === 1 ? '' : 's'} processing,{' '}
          {summary.documents_failed} failed
        </p>
      )}
      {missingText && (
        <p className="flex items-center gap-1.5 text-xs text-amber-700 mb-3">
          <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
          {missingText} {missingAnswers + missingDocuments === 1 ? 'has' : 'have'} no embeddings.
        </p>
      )}
      {summaryError ? <InlineError message="Training and flag counts are unavailable right now." /> : null}

      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={() => {
            setRegenerateNote(null)
            regenerateMutation.reset()
            setRegenerateOpen(true)
          }}
          disabled={!summary || regenerateMutation.isLoading}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <RefreshCwIcon className={`h-3.5 w-3.5 ${regenerateMutation.isLoading ? 'animate-spin' : ''}`} />
          Regenerate embeddings
        </button>
        {regenerateNote && <p className="text-xs text-gray-500">{regenerateNote}</p>}
      </div>
      {regenerateMutation.isError && (
        <InlineError message={errorMessage(regenerateMutation.error, 'Failed to start regenerating embeddings.')} />
      )}

      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-5 mb-2">Most recent open flags</p>
      {flagsLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : flagsError ? (
        <p className="text-sm text-gray-400">Flags are unavailable right now.</p>
      ) : recentFlags.length === 0 ? (
        <p className="text-sm text-gray-400">No open flags.</p>
      ) : (
        <div className="divide-y divide-gray-50 border border-gray-100 rounded-lg">
          {recentFlags.map((flag) => (
            <div key={flag.id} className="px-4 py-3 flex items-start justify-between gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-sm text-gray-800 truncate">{flag.question}</p>
                <p className="text-xs text-gray-400 mt-0.5">
                  {flag.flagged_by?.name || flag.flagged_by?.email || 'Unknown user'} · {formatDate(flag.created_at)}
                </p>
              </div>
              <span className="inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-700 flex-shrink-0">
                {flag.reason_category}
              </span>
            </div>
          ))}
        </div>
      )}

      <ConfirmationModal
        isOpen={regenerateOpen}
        onClose={() => !regenerateMutation.isLoading && setRegenerateOpen(false)}
        onConfirm={() => regenerateMutation.mutate()}
        title="Regenerate embeddings"
        message={`Re-embeds all ${plural(summary?.saved_answers ?? 0, 'saved answer')} and ${plural(documentTotal, 'document')} for this tenant. The agent keeps answering with the current embeddings until each item finishes.`}
        confirmText="Regenerate"
        type="warning"
        isLoading={regenerateMutation.isLoading}
      />
    </Section>
  )
}

// ── Main tab ─────────────────────────────────────────────────────────────────

export default function TenantDomainExpertsTab({ tenantId }: TenantDomainExpertsTabProps) {
  const { data: view, isLoading, error } = useQuery({
    queryKey: ['domainExpert', tenantId],
    queryFn: () => apiClient.getDomainExpert(tenantId),
    refetchOnWindowFocus: false,
    retry: 1,
  })

  if (isLoading) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl px-6 py-12 text-center">
        <RefreshCwIcon className="h-6 w-6 text-gray-400 mx-auto mb-2 animate-spin" />
        <p className="text-sm text-gray-500">Loading Domain Expert Agents...</p>
      </div>
    )
  }

  if (error || !view) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl px-6 py-8 text-center">
        <AlertTriangle className="h-8 w-8 text-red-400 mx-auto mb-2" />
        <p className="text-sm text-red-700">{errorMessage(error, 'Failed to load Domain Expert Agents settings.')}</p>
      </div>
    )
  }

  const appSubdomain = view.app?.app_subdomain || view.sign_in.app_subdomain
  const loginUrl = view.sign_in.login_url || `https://${appSubdomain}.${EXPERT_APP_DOMAIN}/login`

  return (
    <div>
      <SignInSection tenantId={tenantId} settings={view.sign_in} />
      <AppAddressSection tenantId={tenantId} appSubdomain={appSubdomain} loginUrl={loginUrl} />
      <ExpertsSection tenantId={tenantId} passwordLoginEnabled={view.sign_in.password_login_enabled} />
      <SummarySection
        tenantId={tenantId}
        counts={view.counts}
        enabledAt={view.enabled_at}
        enabledByEmail={view.enabled_by_email}
      />
    </div>
  )
}
